import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Upload, X, Loader2, ArrowLeft, ArrowRight, CheckCircle2, Copy } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL_SERVICES = ["Security Guard", "Armed Guard / Gunman", "Bouncer", "Security Supervisor", "Event Security", "Corporate Security", "Residential Security", "Industrial Security", "Hospital Security", "Mall Security", "School / College Security", "Bank / ATM Security", "Construction Site Security", "Other"];
const BIZ_TYPES = ["Proprietorship", "Partnership", "LLP", "Private Limited", "Public Limited"];
const DOC_TYPES = ["Company Registration", "GST Certificate", "PAN", "PSARA Licence", "Owner KYC", "Address Proof", "Other Documents"];
const STEPS = ["Company", "Business & Licence", "Address & Areas", "Services & Manpower", "Documents", "Review"];

const emailOk = (v) => /^\S+@\S+\.\S+$/.test(v);
const mobileOk = (v) => /^[6-9]\d{9}$/.test(String(v).replace(/\s/g, ""));
const pinOk = (v) => /^\d{6}$/.test(v);

// Field + error components live at MODULE scope on purpose. Declaring them inside
// AgencyRegister() made them a new component type on every render, so React remounted
// every <Input> on each keystroke -> focus lost -> mobile keyboard closed.
const Err = ({ k, errs }) => errs[k] ? <p className="text-xs text-red-500 mt-1" data-testid={`err-${k}`}>❌ {errs[k]}</p> : null;
const F = ({ label, k, type = "text", req, ph, full, f, errs, set }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="text-xs font-semibold text-navy-600">{label}{req && " *"}</Label>
    <Input type={type} className={`mt-1 ${errs[k] ? "border-red-400" : ""}`} placeholder={ph} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`ar-${k}`} />
    <Err k={k} errs={errs} />
  </div>
);

export default function AgencyRegister() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [errs, setErrs] = useState({});
  const logoRef = useRef();
  const [logoFile, setLogoFile] = useState(null);
  const [docs, setDocs] = useState([]);
  const [f, setF] = useState({
    name: "", owner_name: "", contact_person: "", mobile: "", email: user?.email || "", password: "",
    established_year: "", description: "",
    business_type: "", registration_number: "", pan: "", gst_number: "", psara_number: "", license_validity: "", years_experience: "",
    address: "", state: "", city: "", district: "", pincode: "",
    service_locations: "", service_states: "", service_districts: "", service_pincodes: "",
    services: [], male_guards: "", female_guards: "", supervisors: "", bouncers: "", armed_personnel: "", available_guards: "",
    price_per_guard_month: 15000,
  });
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setErrs((e) => ({ ...e, [k]: undefined })); };
  const toArr = (v) => String(v || "").split(",").map((s) => s.trim()).filter(Boolean);

  // Open at the top with the first field focused so the form is immediately usable.
  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => document.querySelector('[data-testid="ar-name"]')?.focus(), 120);
    return () => clearTimeout(t);
  }, []);

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!f.name.trim()) e.name = "Agency name is required";
      if (!f.owner_name.trim()) e.owner_name = "Owner name is required";
      if (!mobileOk(f.mobile)) e.mobile = "Enter a valid 10-digit mobile number";
      if (!emailOk(f.email)) e.email = "Enter a valid email";
      if (!user && (f.password || "").length < 6) e.password = "Password must be at least 6 characters";
    }
    if (step === 1) {
      if (f.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(f.pan)) e.pan = "Invalid PAN format (e.g. ABCDE1234F)";
      if (f.gst_number && f.gst_number.length !== 15) e.gst_number = "GST must be 15 characters";
    }
    if (step === 2) {
      if (!f.address.trim()) e.address = "Office address is required";
      if (!f.state.trim()) e.state = "State is required";
      if (!f.city.trim()) e.city = "City is required";
      if (!pinOk(f.pincode)) e.pincode = "Enter a valid 6-digit pincode";
    }
    if (step === 3 && f.services.length === 0) e.services = "Select at least one service";
    setErrs(e);
    if (Object.keys(e).length) { toast.error(Object.values(e)[0]); window.scrollTo({ top: 0, behavior: "smooth" }); return false; }
    return true;
  };
  const next = () => { if (validateStep()) { setStep((s) => Math.min(s + 1, STEPS.length - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); } };
  const back = () => { setStep((s) => Math.max(s - 1, 0)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    if (!validateStep()) return;
    setBusy(true);
    try {
      const agency = {
        name: f.name, owner_name: f.owner_name, contact_person: f.contact_person, mobile: f.mobile, email: f.email,
        established_year: Number(f.established_year) || 2015, description: f.description,
        business_type: f.business_type, registration_number: f.registration_number, pan: f.pan.toUpperCase(),
        gst_number: f.gst_number, psara_number: f.psara_number, license_validity: f.license_validity,
        years_experience: Number(f.years_experience) || 0, price_per_guard_month: Number(f.price_per_guard_month) || 15000,
        head_office: { address: f.address, state: f.state, city: f.city, district: f.district, pincode: f.pincode },
        district: f.district, pincode: f.pincode,
        service_locations: toArr(f.service_locations), service_states: toArr(f.service_states),
        service_districts: toArr(f.service_districts), service_pincodes: toArr(f.service_pincodes),
        service_cities: toArr(f.service_locations), services: f.services,
        male_guards: Number(f.male_guards) || 0, female_guards: Number(f.female_guards) || 0,
        supervisors: Number(f.supervisors) || 0, bouncers: Number(f.bouncers) || 0,
        armed_personnel: Number(f.armed_personnel) || 0, available_guards: Number(f.available_guards) || 0,
      };
      const account = user ? null : { name: f.owner_name || f.name, email: f.email, password: f.password };
      const { data } = await api.post("/agencies/signup", { account, agency });
      if (logoFile) {
        try {
          const fd = new FormData(); fd.append("file", logoFile); fd.append("kind", "logo");
          const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
          await api.put("/agencies/mine/profile", { ...agency, logo: up.data.url });
        } catch { /* logo optional */ }
      }
      for (const d of docs) {
        if (!d.file || !d.doc_type) continue;
        try {
          const fd = new FormData(); fd.append("file", d.file); fd.append("kind", "document");
          const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
          await api.post("/agencies/documents", { doc_type: d.doc_type, expiry_date: d.expiry || "", file_url: up.data.url, file_path: up.data.path, file_id: up.data.id, original_filename: up.data.original_filename });
        } catch { /* continue */ }
      }
      await checkAuth?.();
      setDone(data.reference_id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed. Please check your details and try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-navy-50 flex items-center justify-center px-6 py-16">
        <div className="bg-white border border-navy-200 rounded-2xl p-10 max-w-lg text-center" data-testid="agency-register-success">
          <CheckCircle2 className="mx-auto text-emerald-500" size={56} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-navy-900">Registration Submitted Successfully</h1>
          <p className="mt-2 text-navy-600">Your agency registration has been received by GuardDekho. Our team will review your details and documents.</p>
          <div className="mt-5 inline-flex items-center gap-2 bg-navy-50 border border-navy-200 rounded-lg px-4 py-2">
            <span className="text-sm text-navy-500">Reference ID:</span>
            <span className="font-mono font-bold text-brand" data-testid="ref-id">{done}</span>
            <button onClick={() => { navigator.clipboard.writeText(done); toast.success("Copied"); }} className="text-navy-400 hover:text-brand"><Copy size={14} /></button>
          </div>
          <p className="mt-4 text-sm text-navy-500">Status: <span className="font-semibold text-amber-600">Pending Verification</span></p>
          <Button className="mt-6 bg-brand hover:bg-brand-600 h-11 px-8" onClick={() => navigate("/agency")} data-testid="go-dashboard-btn">Go to Agency Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-50">
      <Seo
        title="Register Your Security Agency — Free Listing | GuardDekho.com"
        description="Register your security agency on GuardDekho.com to get verified, receive qualified leads and win contracts. Free multi-step signup with PSARA/GST document upload."
      />
      <div className="bg-navy-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-navy-300 hover:text-white mb-3"><ArrowLeft size={16} /> Back</button>
          <span className="brand-label text-brand flex items-center gap-2"><Building2 size={16} /> Join GuardDekho</span>
          <h1 className="mt-1.5 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Register your Security Agency</h1>
          <div className="mt-4 flex items-center gap-1.5">
            {STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-brand" : "bg-navy-700"}`} />)}
          </div>
          <p className="mt-2 text-sm text-navy-300">Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="bg-white border border-navy-200 rounded-xl p-6" data-testid="agency-register-form">
          {step === 0 && (<div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <F f={f} errs={errs} set={set} label="Agency / Company Name" k="name" req />
              <F f={f} errs={errs} set={set} label="Owner / Director Name" k="owner_name" req />
              <F f={f} errs={errs} set={set} label="Contact Person" k="contact_person" />
              <F f={f} errs={errs} set={set} label="Mobile Number" k="mobile" req ph="10-digit mobile" />
              <F f={f} errs={errs} set={set} label="Email" k="email" req type="email" />
              {!user && <F f={f} errs={errs} set={set} label="Password" k="password" req type="password" ph="min 6 characters" />}
              <F f={f} errs={errs} set={set} label="Year Established" k="established_year" type="number" />
            </div>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl border border-navy-200 bg-navy-50 flex items-center justify-center overflow-hidden shrink-0">
                {logoFile ? <img src={URL.createObjectURL(logoFile)} alt="logo" className="w-full h-full object-contain" /> : <Building2 className="text-navy-300" />}
              </div>
              <div>
                <Label className="text-xs font-semibold text-navy-600">Company Logo</Label>
                <input ref={logoRef} type="file" accept="image/*" hidden onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="ar-logo-input" />
                <div className="mt-1"><Button type="button" variant="outline" className="border-navy-300" onClick={() => logoRef.current?.click()} data-testid="ar-logo-btn"><Upload size={15} className="mr-1" /> Upload logo</Button></div>
              </div>
            </div>
            <div><Label className="text-xs font-semibold text-navy-600">Company Description</Label><Textarea rows={3} className="mt-1" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="ar-description" /></div>
          </div>)}

          {step === 1 && (<div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs font-semibold text-navy-600">Business Type</Label>
              <Select value={f.business_type} onValueChange={(v) => set("business_type", v)}><SelectTrigger className="mt-1" data-testid="ar-business_type"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{BIZ_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select></div>
            <F f={f} errs={errs} set={set} label="Company Registration Number" k="registration_number" />
            <F f={f} errs={errs} set={set} label="PAN Number" k="pan" ph="ABCDE1234F" />
            <F f={f} errs={errs} set={set} label="GST Number" k="gst_number" />
            <F f={f} errs={errs} set={set} label="PSARA Licence Number" k="psara_number" />
            <F f={f} errs={errs} set={set} label="PSARA Licence Expiry" k="license_validity" type="date" />
            <F f={f} errs={errs} set={set} label="Years of Experience" k="years_experience" type="number" />
            <F f={f} errs={errs} set={set} label="Price / guard / month (₹)" k="price_per_guard_month" type="number" />
          </div>)}

          {step === 2 && (<div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <F f={f} errs={errs} set={set} label="Office Address" k="address" req full />
              <F f={f} errs={errs} set={set} label="State" k="state" req />
              <F f={f} errs={errs} set={set} label="City" k="city" req />
              <F f={f} errs={errs} set={set} label="District" k="district" />
              <F f={f} errs={errs} set={set} label="Pincode" k="pincode" req ph="6 digits" />
            </div>
            <p className="brand-label text-navy-400 pt-2">Service areas (used for lead matching)</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs font-semibold text-navy-600">Cities served (comma separated)</Label><Input className="mt-1" placeholder="Ahmedabad, Surat" value={f.service_locations} onChange={(e) => set("service_locations", e.target.value)} data-testid="ar-service_locations" /></div>
              <div><Label className="text-xs font-semibold text-navy-600">States (comma separated)</Label><Input className="mt-1" placeholder="Gujarat" value={f.service_states} onChange={(e) => set("service_states", e.target.value)} data-testid="ar-service_states" /></div>
              <div><Label className="text-xs font-semibold text-navy-600">Districts (comma separated)</Label><Input className="mt-1" value={f.service_districts} onChange={(e) => set("service_districts", e.target.value)} data-testid="ar-service_districts" /></div>
              <div><Label className="text-xs font-semibold text-navy-600">Pincodes served (comma separated)</Label><Input className="mt-1" placeholder="380001, 395001" value={f.service_pincodes} onChange={(e) => set("service_pincodes", e.target.value)} data-testid="ar-service_pincodes" /></div>
            </div>
          </div>)}

          {step === 3 && (<div className="space-y-5">
            <div>
              <Label className="text-xs font-semibold text-navy-600">Services Offered *</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ALL_SERVICES.map((o) => { const on = f.services.includes(o); return (
                  <button type="button" key={o} onClick={() => set("services", on ? f.services.filter((x) => x !== o) : [...f.services, o])}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-brand text-white border-brand" : "border-navy-300 text-navy-700 hover:border-brand"}`}
                    data-testid={`svc-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{o}</button>); })}
              </div>
              <Err errs={errs} k="services" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-navy-600">Manpower</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-2">
                <F f={f} errs={errs} set={set} label="Male Guards" k="male_guards" type="number" />
                <F f={f} errs={errs} set={set} label="Female Guards" k="female_guards" type="number" />
                <F f={f} errs={errs} set={set} label="Supervisors" k="supervisors" type="number" />
                <F f={f} errs={errs} set={set} label="Bouncers" k="bouncers" type="number" />
                <F f={f} errs={errs} set={set} label="Armed Personnel" k="armed_personnel" type="number" />
                <F f={f} errs={errs} set={set} label="Available Guards" k="available_guards" type="number" />
              </div>
            </div>
          </div>)}

          {step === 4 && (<div className="space-y-4">
            <p className="text-sm text-navy-500">Upload verification documents (PDF or image). You can also add these later from your dashboard.</p>
            {docs.map((d, i) => (
              <div key={i} className="flex flex-wrap items-end gap-3 border border-navy-100 rounded-lg p-3" data-testid={`doc-${i}`}>
                <div className="flex-1 min-w-[150px]"><Label className="text-xs">Type</Label>
                  <Select value={d.doc_type} onValueChange={(v) => setDocs(docs.map((x, j) => j === i ? { ...x, doc_type: v } : x))}><SelectTrigger data-testid={`doc-type-${i}`}><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Expiry</Label><Input type="date" value={d.expiry} onChange={(e) => setDocs(docs.map((x, j) => j === i ? { ...x, expiry: e.target.value } : x))} /></div>
                <div><Label className="text-xs">File</Label><Input type="file" accept="image/*,application/pdf" onChange={(e) => setDocs(docs.map((x, j) => j === i ? { ...x, file: e.target.files?.[0] } : x))} data-testid={`doc-file-${i}`} /></div>
                <Button type="button" variant="ghost" className="text-red-500" onClick={() => setDocs(docs.filter((_, j) => j !== i))}><X size={16} /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" className="border-navy-300" onClick={() => setDocs([...docs, { doc_type: "", expiry: "", file: null }])} data-testid="add-doc-btn"><Upload size={15} className="mr-1" /> Add Document</Button>
          </div>)}

          {step === 5 && (<div className="space-y-3 text-sm" data-testid="review-section">
            <h3 className="font-heading font-semibold text-navy-900">Review your details</h3>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-navy-700">
              <div><span className="text-navy-400">Agency:</span> {f.name}</div>
              <div><span className="text-navy-400">Owner:</span> {f.owner_name}</div>
              <div><span className="text-navy-400">Mobile:</span> {f.mobile}</div>
              <div><span className="text-navy-400">Email:</span> {f.email}</div>
              <div><span className="text-navy-400">PSARA:</span> {f.psara_number || "—"}</div>
              <div><span className="text-navy-400">Location:</span> {f.city}, {f.state} {f.pincode}</div>
              <div className="sm:col-span-2"><span className="text-navy-400">Services:</span> {f.services.join(", ") || "—"}</div>
              <div className="sm:col-span-2"><span className="text-navy-400">Documents:</span> {docs.filter((d) => d.file).length} attached</div>
            </div>
            <p className="text-xs text-navy-500 pt-2">By submitting, your agency profile will be created with status <b>Pending Verification</b>. The Verified badge is granted only after admin approval.</p>
          </div>)}

          <div className="flex items-center justify-between mt-6 pt-5 border-t border-navy-100">
            <Button type="button" variant="outline" className="border-navy-300" disabled={step === 0} onClick={back} data-testid="step-back"><ArrowLeft size={15} className="mr-1" /> Back</Button>
            {step < STEPS.length - 1
              ? <Button type="button" className="bg-navy-900 hover:bg-navy-800" onClick={next} data-testid="step-next">Next <ArrowRight size={15} className="ml-1" /></Button>
              : <Button type="button" className="bg-brand hover:bg-brand-600 font-semibold" disabled={busy} onClick={submit} data-testid="ar-submit">{busy ? <Loader2 size={16} className="animate-spin mr-1" /> : <CheckCircle2 size={16} className="mr-1" />} Submit Agency Registration</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
